/**
 * VAT validation composable for EU countries
 * Based on the EU VAT number format validation
 */
export function useVatValidation() {
    /**
     * Normalize VAT number by removing spaces and converting to uppercase
     */
    function normalize(number: string | null | undefined): string | null {
        if (!number || typeof number !== 'string') {
            return null;
        }
        // Remove spaces and convert to uppercase
        return number.replace(/\s/g, '').toUpperCase();
    }

    /**
     * VAT number patterns for EU countries
     */
    const vatPatterns: Record<string, RegExp> = {
        'AT': /^ATU\d{8}$/,
        'BE': /^BE[0-1]{1}\d{9}$/,
        'BG': /^BG\d{9,10}$/,
        'CY': /^CY\d{8}[A-Z]{1}$/,
        'CZ': /^CZ\d{8,10}$/,
        'DE': /^DE\d{9}$/,
        'DK': /^DK\d{8}$/,
        'EE': /^EE\d{9}$/,
        'EL': /^EL\d{9}$/,
        'ES': /^ES[A-Z0-9]{1}\d{7}[A-Z0-9]{1}$/,
        'FI': /^FI\d{8}$/,
        'FR': /^FR[A-Z0-9]{2}\d{9}$/,
        'HR': /^HR\d{11}$/,
        'HU': /^HU\d{8}$/,
        'IE': /^IE[A-Z0-9+*]{8,9}$/,
        'IT': /^IT\d{11}$/,
        'LT': /^LT\d{9,12}$/,
        'LU': /^LU\d{8}$/,
        'LV': /^LV\d{11}$/,
        'MT': /^MT\d{8}$/,
        'NL': /^NL[A-Z0-9+*]{12}$/,
        'PL': /^PL\d{10}$/,
        'PT': /^PT\d{9}$/,
        'RO': /^RO\d{2,10}$/,
        'SE': /^SE\d{12}$/,
        'SI': /^SI\d{8}$/,
        'SK': /^SK\d{10}$/,
        'XI': /^XI[A-Z0-9]{5,12}$/
    };

    /**
     * Validate VAT number for a specific country
     * @param number VAT number to validate
     * @param countryCode ISO 3166-1 alpha-2 country code (e.g., 'DE', 'FR', 'AT')
     * @returns true if valid, false otherwise
     */
    function isValid(number: string | null | undefined, countryCode?: string): boolean {
        const normalizedNumber = normalize(number);

        if (!normalizedNumber) {
            return false;
        }

        // If country code is provided, use it to determine the pattern
        if (countryCode) {
            const upperCountryCode = countryCode.toUpperCase();
            const pattern = vatPatterns[upperCountryCode];

            if (pattern) {
                return pattern.test(normalizedNumber);
            }
        }

        // If no country code provided, try to extract from VAT number
        const detectedCountryCode = normalizedNumber.substring(0, 2);
        const pattern = vatPatterns[detectedCountryCode];

        if (pattern) {
            return pattern.test(normalizedNumber);
        }

        return false;
    }

    /**
     * Get the country code from a VAT number
     * @param number VAT number
     * @returns country code or null
     */
    function getCountryCode(number: string | null | undefined): string | null {
        const normalizedNumber = normalize(number);

        if (!normalizedNumber || normalizedNumber.length < 2) {
            return null;
        }

        const countryCode = normalizedNumber.substring(0, 2);

        if (vatPatterns[countryCode]) {
            return countryCode;
        }

        return null;
    }

    /**
     * Get validation pattern for a country
     * @param countryCode ISO 3166-1 alpha-2 country code
     * @returns RegExp pattern or null
     */
    function getPattern(countryCode: string): RegExp | null {
        const upperCountryCode = countryCode.toUpperCase();
        return vatPatterns[upperCountryCode] || null;
    }

    return {
        isValid,
        normalize,
        getCountryCode,
        getPattern,
        vatPatterns
    };
}

